import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import type * as Paths from "./paths";

interface OperationMethods {
  "getMarketplaces"(
    parameters?: Parameters<Paths.GetMarketplaces.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetMarketplaces.Responses.$200>;

  "createMarketplace"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateMarketplace.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateMarketplace.Responses.$200>;

  "getMarketplace"(
    parameters?: Parameters<Paths.GetMarketplace.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetMarketplace.Responses.$200>;

  "updateMarketplace"(
    parameters?: Parameters<Paths.UpdateMarketplace.PathParameters> | null,
    data?: Paths.UpdateMarketplace.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateMarketplace.Responses.$200>;

  "deleteMarketplace"(
    parameters?: Parameters<Paths.DeleteMarketplace.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteMarketplace.Responses.$204>;
  "getNewForm"(
    parameter?: Parameters<UnknownParamsObject> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetNewForm.Responses.$200>;
}

export const getMarketplaceOperations = (client: AxiosInstance) => {
  const getMarketplaces: OperationMethods["getMarketplaces"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/marketplace";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const getNewForm: OperationMethods["getNewForm"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/marketplace/new-form";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const createMarketplace: OperationMethods["createMarketplace"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/marketplace";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const getMarketplace: OperationMethods["getMarketplace"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/marketplace/{marketplace}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const updateMarketplace: OperationMethods["updateMarketplace"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/marketplace/{marketplace}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const deleteMarketplace: OperationMethods["deleteMarketplace"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/marketplace/{marketplace}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  return {
    getMarketplaces: getMarketplaces,
    createMarketplace: createMarketplace,
    getMarketplace: getMarketplace,
    updateMarketplace: updateMarketplace,
    deleteMarketplace: deleteMarketplace,
    getNewForm: getNewForm,
  };
};
